import React, { useEffect, useState, useRef } from "react";
import { Activity, Flame, Droplet, HeartPulse, Plus, Loader2, TrendingUp, Play, Square, Stethoscope, Sparkles, AlertTriangle, Info, Trash2, Phone, Users, Syringe, Pill, Baby, UserRound, MapPin, Star, ArrowRight, X, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";

// -----------------------------------------------------------------------------
// Health — 3 mini-modules:
//  1. Step counter using the DeviceMotion API (accelerometer) — best-effort on
//     web (works on Android/Chrome; iOS Safari needs a permission tap).
//  2. Blood test log — log any marker, keep a per-marker trend list.
//  3. Health issues journal — ongoing / resolved conditions.
// -----------------------------------------------------------------------------
export default function Health() {
  const { user } = useAuth();
  const [tab, setTab] = useState("steps");
  const [age, setAge] = useState("all");                 // life-stage chip filter
  useSeo({ title:"Health · Steps · Calories · Blood · Issues · find2near",
           description:"Daily steps, calorie burn, blood-test trend log and health-issue journal — private to your account.", canonical:"/health" });

  // Tabs ordered by life-stage relevance: Kids/Teens → Adults → Seniors.
  // Each tab lists the audiences it's most useful for, so the age-chip
  // above can visually highlight the tabs relevant to that group.
  const TABS = [
    { k:"family",  l:"Family book",        I:Users,       forAges:["kids","teens","adults","seniors"] },
    { k:"steps",   l:"Steps + Calories",   I:Activity,    forAges:["kids","teens","adults","seniors"] },
    { k:"symptom", l:"AI Symptom Check",   I:Stethoscope, forAges:["kids","teens","adults","seniors"] },
    { k:"blood",   l:"Blood tests",        I:Droplet,     forAges:["adults","seniors"] },
    { k:"issues",  l:"Health issues",      I:HeartPulse,  forAges:["adults","seniors"] },
  ];
  const AGES = [
    { k:"all",     l:"All ages" },
    { k:"kids",    l:"Kids" },
    { k:"teens",   l:"Teens" },
    { k:"adults",  l:"Adults" },
    { k:"seniors", l:"Senior citizens" },
  ];
  const isRelevant = (t) => age === "all" || t.forAges.includes(age);
  const shownTabs  = TABS.filter(isRelevant);
  // If the current tab is filtered out by the age chip, jump to the first
  // relevant tab so the user isn't stuck on an empty pane.
  // NOTE: Must be declared BEFORE any early return to satisfy Rules of Hooks.
  useEffect(() => { if (!shownTabs.find(t => t.k === tab) && shownTabs[0]) setTab(shownTabs[0].k); }, [age]); // eslint-disable-line

  if (!user) return <div className="max-w-md mx-auto text-center py-24 px-6">
    <HeartPulse className="mx-auto text-rose-500" size={44}/>
    <h2 className="heading text-2xl font-black mt-4">Sign in to open Health</h2>
    <p className="text-stone-500 mt-2 text-sm">Your data stays private and scoped to your account.</p>
  </div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6" data-testid="health-page">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><HeartPulse size={12} className="text-rose-600"/> Health</div>
      <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Track. <span className="text-rose-600">Trend. Take action.</span></h1>

      {/* Life-stage chip filter — Kids → Seniors */}
      <div className="flex gap-1 my-3 flex-wrap items-center" data-testid="health-agechips">
        <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mr-1">For</span>
        {AGES.map(a => (
          <button
            key={a.k}
            onClick={() => setAge(a.k)}
            className={`text-[11px] font-bold rounded-full px-2.5 py-1 border transition-colors ${age===a.k?"bg-rose-600 text-white border-rose-600":"bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
            data-testid={`health-age-${a.k}`}
          >
            {a.l}
          </button>
        ))}
      </div>

      <div className="flex gap-1.5 my-4 flex-wrap">
        {shownTabs.map(({k,l,I}) => (
          <button key={k} onClick={()=>setTab(k)} className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border ${tab===k?"bg-stone-900 text-white border-stone-900":"bg-white text-stone-700 border-stone-200"}`} data-testid={`health-tab-${k}`}><I size={12}/> {l}</button>
        ))}
      </div>

      {tab==="family"  && <FamilyTab/>}
      {tab==="steps"   && <StepsTab/>}
      {tab==="symptom" && <SymptomTab/>}
      {tab==="blood"   && <BloodTab/>}
      {tab==="issues"  && <IssuesTab/>}
    </div>
  );
}

// ── STEPS -------------------------------------------------------------------
function StepsTab() {
  const [live, setLive] = useState({ counting:false, steps:0 });
  const [today, setToday] = useState(0);
  const [summary, setSummary] = useState({ days:0, total_steps:0, total_kcal:0, entries:[] });
  const stepThresholdRef = useRef({ last:0, cooldown:0 });

  const startCounting = async () => {
    // iOS 13+ requires an explicit permission request on a user gesture.
    if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
      const p = await DeviceMotionEvent.requestPermission();
      if (p !== "granted") { toast.error("Motion permission denied"); return; }
    }
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const mag = Math.sqrt((a.x||0)**2 + (a.y||0)**2 + (a.z||0)**2);
      const now = Date.now();
      // Naive peak detector: > 13 m/s² with 300 ms cool-down between steps.
      if (mag > 13 && (now - stepThresholdRef.current.cooldown) > 300) {
        stepThresholdRef.current.cooldown = now;
        setLive(s => ({...s, steps: s.steps + 1}));
      }
    };
    window.addEventListener("devicemotion", onMotion);
    setLive({ counting:true, steps:0, _stop: () => window.removeEventListener("devicemotion", onMotion) });
  };
  const stopCounting = async () => {
    live._stop?.();
    const total = today + live.steps;
    setToday(total);
    setLive({ counting:false, steps:0 });
    // Save today's number.
    try {
      const kcal = total * 0.04;   // ~0.04 kcal per step for a 70 kg adult
      await api.post("/health/steps", { date: new Date().toISOString().slice(0,10), steps: total, kcal });
      toast.success(`Saved ${total} steps`); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const load = () => api.get("/health/steps", { params:{ days:14 } }).then(r=>{ setSummary(r.data); const t = r.data.entries?.find(x=>x.date===new Date().toISOString().slice(0,10)); if (t) setToday(t.steps); }).catch(()=>{});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="rounded-3xl bg-gradient-to-br from-rose-500 to-red-700 text-white p-5">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Today</div>
        <div className="text-5xl font-black font-mono mt-1" data-testid="steps-today">{(today+live.steps).toLocaleString()}</div>
        <div className="text-xs opacity-80">steps · ≈ {((today+live.steps)*0.04).toFixed(0)} kcal · <Flame size={11} className="inline"/></div>
        {live.counting
          ? <Button onClick={stopCounting} className="mt-3 rounded-full bg-white text-red-700 hover:bg-white/90 font-black" data-testid="steps-stop"><Square size={13} className="mr-1.5"/> Stop counting</Button>
          : <Button onClick={startCounting} className="mt-3 rounded-full bg-white text-red-700 hover:bg-white/90 font-black" data-testid="steps-start"><Play size={13} className="mr-1.5"/> Start counting</Button>}
      </div>
      <div className="rounded-3xl bg-white border border-stone-200 p-4">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 inline-flex items-center gap-2"><TrendingUp size={12}/> Last 14 days</div>
        <div className="grid grid-cols-7 gap-1">
          {summary.entries.slice(0,14).reverse().map((e,i)=>(
            <div key={i} className="text-center">
              <div className="h-16 flex items-end justify-center"><div className="w-full bg-rose-500 rounded-t" style={{ height:`${Math.min(100, (e.steps/10000)*100)}%` }}/></div>
              <div className="text-[9px] font-mono text-stone-500 mt-1">{e.date.slice(5)}</div>
              <div className="text-[9px] font-bold">{e.steps}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-stone-500">Total: <b className="text-stone-900">{summary.total_steps.toLocaleString()} steps</b> · {Math.round(summary.total_kcal)} kcal</div>
      </div>
      <div className="text-[10px] text-stone-500">Note: Web-based step counting relies on your device's accelerometer and can miss ~10-15% of real steps. Keep the phone in your pocket / holster while counting.</div>
    </div>
  );
}

// ── BLOOD -------------------------------------------------------------------
function BloodTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ date:new Date().toISOString().slice(0,10), marker:"Fasting Glucose", value:"", unit:"mg/dL", ref_low:"", ref_high:"", lab_name:"" });
  const load = () => api.get("/health/blood-tests").then(r=>setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!f.value) { toast.error("Value required"); return; }
    try {
      await api.post("/health/blood-tests", { ...f, value: parseFloat(f.value), ref_low: f.ref_low? parseFloat(f.ref_low):null, ref_high: f.ref_high? parseFloat(f.ref_high):null });
      toast.success("Logged"); load(); setF({...f, value:""});
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 grid gap-2 md:grid-cols-6">
        <Input value={f.date} onChange={e=>setF({...f,date:e.target.value})} type="date" className="rounded-2xl h-10 col-span-2 font-mono"/>
        <Input value={f.marker} onChange={e=>setF({...f,marker:e.target.value})} placeholder="Marker" className="rounded-2xl h-10 col-span-2"/>
        <Input value={f.value} onChange={e=>setF({...f,value:e.target.value})} placeholder="Value" type="number" step="0.01" className="rounded-2xl h-10 font-mono"/>
        <Input value={f.unit} onChange={e=>setF({...f,unit:e.target.value})} placeholder="Unit" className="rounded-2xl h-10 font-mono"/>
        <Input value={f.ref_low}  onChange={e=>setF({...f,ref_low:e.target.value})}  placeholder="Ref low"  type="number" step="0.01" className="rounded-2xl h-10 font-mono"/>
        <Input value={f.ref_high} onChange={e=>setF({...f,ref_high:e.target.value})} placeholder="Ref high" type="number" step="0.01" className="rounded-2xl h-10 font-mono"/>
        <Input value={f.lab_name} onChange={e=>setF({...f,lab_name:e.target.value})} placeholder="Lab name" className="rounded-2xl h-10 col-span-3"/>
        <Button onClick={submit} className="rounded-full bg-rose-600 hover:bg-rose-700 font-black col-span-1"><Plus size={13} className="mr-1"/> Log</Button>
      </div>
      <div className="rounded-3xl bg-white border border-stone-200 divide-y divide-stone-100">
        {items.length === 0 ? <div className="p-6 text-center text-stone-400 text-sm">No entries yet.</div> : items.map(t => {
          const out = (t.ref_low && t.value < t.ref_low) || (t.ref_high && t.value > t.ref_high);
          return (<div key={t.id} className="p-3 flex items-center gap-3 text-sm">
            <span className="text-[10px] font-mono text-stone-500 w-24">{t.date}</span>
            <span className="font-bold flex-1">{t.marker}</span>
            <span className={`font-mono font-black ${out ? "text-rose-600" : "text-emerald-700"}`}>{t.value} {t.unit}</span>
            {t.ref_low != null && <span className="text-[10px] text-stone-500 font-mono">{t.ref_low}-{t.ref_high}</span>}
          </div>);
        })}
      </div>
    </div>
  );
}

// ── ISSUES ------------------------------------------------------------------
function IssuesTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title:"", started:new Date().toISOString().slice(0,10), ongoing:true, ended:"", severity:"moderate", notes:"" });
  const load = () => api.get("/health/issues").then(r=>setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try { await api.post("/health/issues", f); toast.success("Logged"); load(); setF({...f, title:"", notes:""}); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <Input value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Issue title (e.g. Migraine, PCOD, Diabetes)" className="rounded-2xl h-10"/>
        <div className="grid grid-cols-4 gap-2">
          <Input type="date" value={f.started} onChange={e=>setF({...f,started:e.target.value})} className="rounded-2xl h-10 col-span-1 font-mono"/>
          <select value={f.severity} onChange={e=>setF({...f,severity:e.target.value})} className="rounded-2xl h-10 border border-stone-200 px-3 col-span-1"><option>mild</option><option>moderate</option><option>severe</option></select>
          <label className="inline-flex items-center gap-2 col-span-1 text-sm"><input type="checkbox" checked={f.ongoing} onChange={e=>setF({...f,ongoing:e.target.checked})}/> Ongoing</label>
          {!f.ongoing && <Input type="date" value={f.ended} onChange={e=>setF({...f,ended:e.target.value})} className="rounded-2xl h-10 col-span-1 font-mono"/>}
        </div>
        <Input value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} placeholder="Notes / medication" className="rounded-2xl h-10"/>
        <Button onClick={submit} className="rounded-full bg-rose-600 hover:bg-rose-700 font-black"><Plus size={13} className="mr-1"/> Log</Button>
      </div>
      <div className="rounded-3xl bg-white border border-stone-200 divide-y divide-stone-100">
        {items.length===0 ? <div className="p-6 text-center text-stone-400 text-sm">No health issues logged yet.</div> : items.map(i => (
          <div key={i.id} className="p-3 flex items-center gap-3 text-sm">
            <span className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${i.severity==='severe'?'bg-rose-100 text-rose-800':i.severity==='moderate'?'bg-amber-100 text-amber-800':'bg-emerald-100 text-emerald-800'}`}>{i.severity}</span>
            <span className="font-bold flex-1">{i.title}</span>
            <span className="text-[10px] font-mono text-stone-500">{i.started}{i.ended ? " → "+i.ended : (i.ongoing ? " → ongoing" : "")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AI SYMPTOM CHECKER -----------------------------------------------------
//   NOT a diagnosis — clinical *triage* assistant. Renders the LLM triage
//   output as urgency badge + possible causes + red-flag list + self-care.
function SymptomTab() {
  const [symptoms, setSymptoms] = useState("");
  const [age, setAge]   = useState("");
  const [sex, setSex]   = useState("");
  const [dur, setDur]   = useState("");
  const [chronic, setChronic] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [checkId, setCheckId] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/health/symptom-check/mine").then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const submit = async () => {
    if (symptoms.trim().length < 6) { toast.error("Describe your symptoms in a full sentence"); return; }
    setBusy(true); setResult(null); setCheckId(null);
    try {
      const r = await api.post("/health/symptom-check", {
        symptoms,
        age: age ? Number(age) : undefined,
        sex: sex || undefined,
        duration_days: dur ? Number(dur) : undefined,
        chronic: chronic.split(",").map(s => s.trim()).filter(Boolean),
      });
      setResult(r.data.result || r.data);
      setCheckId(r.data.check_id || null);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Triage failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this triage record?")) return;
    await api.delete(`/health/symptom-check/${id}`);
    loadHistory();
  };

  const U = {
    routine:   { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Routine",   note: "Non-urgent — schedule when convenient." },
    soon:      { cls: "bg-amber-100 text-amber-800 border-amber-200",       label: "Soon",      note: "See a doctor within 24-48 hours." },
    urgent:    { cls: "bg-orange-100 text-orange-800 border-orange-200",    label: "Urgent",    note: "Go to a same-day clinic or urgent care." },
    emergency: { cls: "bg-rose-100 text-rose-900 border-rose-300",          label: "Emergency", note: "Call emergency services immediately." },
  };
  const L = { low: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", high: "bg-rose-100 text-rose-800" };
  const u = result ? (U[result.urgency] || U.routine) : null;

  return (
    <div className="space-y-4" data-testid="symptom-tab">
      <div className="rounded-3xl bg-rose-50 border border-rose-200 p-3 flex items-start gap-2">
        <AlertTriangle size={14} className="text-rose-600 mt-0.5 shrink-0"/>
        <div className="text-xs text-rose-900">
          <b>Not a diagnosis.</b> This AI provides *triage guidance only* — it helps you decide
          how urgently to see a doctor. Never delay professional care based on this output.
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 p-4">
        <div className="text-[11px] uppercase tracking-widest font-black text-stone-600 mb-2">Describe what you feel</div>
        <Textarea
          rows={4}
          value={symptoms}
          onChange={e => setSymptoms(e.target.value)}
          placeholder="e.g. Persistent dry cough for 5 days, mild fever at night, and slight chest tightness on climbing stairs."
          className="rounded-2xl text-sm"
          data-testid="symptom-input"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          <Input placeholder="Age"          value={age}     onChange={e => setAge(e.target.value)}    inputMode="numeric" className="rounded-full text-sm" data-testid="symptom-age"/>
          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger className="rounded-full text-sm" data-testid="symptom-sex"><SelectValue placeholder="Sex"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Days since it started" value={dur}     onChange={e => setDur(e.target.value)} inputMode="numeric" className="rounded-full text-sm" data-testid="symptom-duration"/>
          <Input placeholder="Chronic conditions (comma-separated)" value={chronic} onChange={e => setChronic(e.target.value)} className="rounded-full text-sm" data-testid="symptom-chronic"/>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button onClick={submit} disabled={busy} className="rounded-full bg-rose-600 hover:bg-rose-700 font-black" data-testid="symptom-submit">
            {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Sparkles size={13} className="mr-1"/>}
            Run AI triage
          </Button>
          <div className="text-[11px] text-stone-500 flex items-center gap-1"><Info size={11}/> takes 5–15 seconds</div>
        </div>
      </div>

      {result && (
        <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-3" data-testid="symptom-result">
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest border ${u.cls}`} data-testid={`symptom-urgency-${result.urgency}`}>
            <HeartPulse size={12}/> {u.label}
          </div>
          <div className="text-sm text-stone-700">{u.note}</div>
          {result.urgency === "emergency" && (
            <a href="tel:112" className="inline-flex items-center gap-2 text-sm font-black text-white bg-rose-600 hover:bg-rose-700 rounded-full px-3 py-1.5" data-testid="symptom-emergency-call">
              <Phone size={13}/> Call emergency 112 (India: 102 / 108)
            </a>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Recommended action</div>
            <div className="text-sm text-stone-900 font-bold">{result.recommended_action}</div>
          </div>
          {(result.possible_causes || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Possible causes (not a diagnosis)</div>
              <div className="grid md:grid-cols-2 gap-2">
                {result.possible_causes.map((c, i) => (
                  <div key={i} className="rounded-2xl border border-stone-200 p-2.5" data-testid={`symptom-cause-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${L[c.likelihood] || L.low}`}>{c.likelihood || "low"}</span>
                      <span className="font-bold text-stone-900">{c.name}</span>
                    </div>
                    <div className="text-xs text-stone-600 leading-snug">{c.why}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(result.when_to_seek_care || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Red flags — go to a doctor if any of these appear</div>
              <ul className="text-sm text-stone-800 list-disc pl-5 space-y-0.5">
                {result.when_to_seek_care.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
          {(result.self_care || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Gentle self-care while you wait</div>
              <ul className="text-sm text-stone-700 list-disc pl-5 space-y-0.5">
                {result.self_care.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
          <div className="text-[10px] text-stone-500 italic border-t border-stone-100 pt-2">{result.disclaimer || "AI triage is not a diagnosis."}</div>
        </div>
      )}

      {result && checkId && <DoctorSuggestions checkId={checkId} urgency={result.urgency}/>}

      {history.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="symptom-history">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">Your previous triage runs</div>
          <div className="divide-y divide-stone-100">
            {history.map(h => (
              <div key={h.check_id} className="p-3 flex items-start gap-2">
                <span className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 border ${(U[h.result?.urgency] || U.routine).cls}`}>{h.result?.urgency}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-900 line-clamp-2">{h.symptoms}</div>
                  <div className="text-[10px] font-mono text-stone-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <button onClick={() => del(h.check_id)} className="text-stone-400 hover:text-rose-600" data-testid={`symptom-del-${h.check_id}`}><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── SYMPTOM → DOCTOR ------------------------------------------------------
//   After a triage, ask the backend to suggest nearby doctors matching the
//   detected specialties. Emergency triage widens radius to 50 km and shows
//   emergency helplines banner.
function DoctorSuggestions({ checkId, urgency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Best-effort geolocation — the endpoint gracefully handles missing coords.
    let cancelled = false;
    const fetchDoctors = async (lat, lng) => {
      setLoading(true);
      try {
        const params = lat != null && lng != null ? { lat, lng } : {};
        const r = await api.get(`/health/symptom-check/${checkId}/doctors`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Couldn't fetch nearby doctors");
      } finally { if (!cancelled) setLoading(false); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p => fetchDoctors(p.coords.latitude, p.coords.longitude),
        () => fetchDoctors(),
        { timeout: 3500, maximumAge: 60000 },
      );
    } else fetchDoctors();
    return () => { cancelled = true; };
  }, [checkId]);

  if (loading) return <div className="rounded-3xl bg-white border border-stone-200 p-4 text-center text-xs text-stone-500"><Loader2 className="animate-spin mx-auto mb-1" size={16}/> Finding nearby doctors that match your symptoms…</div>;

  // Emergency helplines ALWAYS render for emergency triage — even if we can't
  // find matching doctors nearby, the user must reach a helpline immediately.
  const helplines = (urgency === "emergency" && data?.emergency_helplines) ? (
    <div className="bg-rose-600 text-white px-4 py-2 flex flex-wrap gap-2 items-center rounded-t-3xl" data-testid="symptom-emergency-helplines">
      <AlertTriangle size={14}/>
      <span className="text-xs font-black uppercase tracking-widest">Emergency — call now:</span>
      {data.emergency_helplines.map(h => (
        <a key={h.number} href={`tel:${h.number}`} className="inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-2.5 py-0.5 text-xs font-black" data-testid={`emergency-call-${h.number}`}>
          <Phone size={11}/> {h.label}: <span className="font-mono">{h.number}</span>
        </a>
      ))}
    </div>
  ) : null;

  if (!data || !data.doctors || data.doctors.length === 0) {
    return (
      <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="symptom-doctors-empty">
        {helplines}
        <div className="p-4">
          <div className="flex items-center gap-2 text-sm">
            <Stethoscope size={14} className="text-stone-400"/>
            <span className="text-stone-700">No matching doctors found within {data?.radius_km || 10} km.</span>
          </div>
          <Link to={`/search?category=doctor&q=${encodeURIComponent((data?.specialties || []).join(" "))}`}
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-rose-700 hover:underline"
                data-testid="symptom-doctors-search-link">
            Browse all doctors <ArrowRight size={11}/>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="symptom-doctors">
      {helplines}
      <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-fuchsia-50 border-b border-stone-200 flex items-center gap-2">
        <Stethoscope size={14} className="text-rose-600"/>
        <div className="text-[11px] uppercase tracking-widest font-black text-stone-700">Nearby doctors that match</div>
        <div className="ml-auto text-[10px] text-stone-500 flex gap-1 flex-wrap">
          {(data.specialties || []).map(s => (
            <span key={s} className="bg-white border border-stone-200 rounded-full px-2 py-0.5 font-bold capitalize">{s.replace(/_/g, " ")}</span>
          ))}
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {data.doctors.map(d => (
          <Link
            to={`/listing/${d.listing_id}`}
            key={d.listing_id}
            className="p-3 flex items-center gap-3 hover:bg-stone-50 transition-colors"
            data-testid={`symptom-doctor-${d.listing_id}`}
          >
            {d.image ? (
              <img src={d.image} alt="" className="w-12 h-12 rounded-2xl object-cover border border-stone-200 shrink-0"/>
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-stone-100 grid place-items-center shrink-0"><UserRound size={20} className="text-stone-400"/></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-stone-900 truncate">{d.title}</div>
              <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap">
                {d.specialty && <span className="capitalize">{d.specialty.replace(/_/g, " ")}</span>}
                {d.city && <span className="inline-flex items-center gap-0.5"><MapPin size={10}/> {d.city}</span>}
                {typeof d.distance_km === "number" && <span>· {d.distance_km} km</span>}
                {typeof d.rating === "number" && <span className="inline-flex items-center gap-0.5 text-amber-600"><Star size={10} className="fill-amber-500"/> {d.rating.toFixed(1)}</span>}
              </div>
            </div>
            {typeof d.price === "number" && d.price > 0 && (
              <div className="text-right shrink-0">
                <div className="text-sm font-black text-stone-900">₹{d.price}</div>
                <div className="text-[10px] text-stone-400">{d.price_unit || "visit"}</div>
              </div>
            )}
            <ArrowRight size={14} className="text-stone-400 shrink-0"/>
          </Link>
        ))}
      </div>
    </div>
  );
}


// ── FAMILY HEALTH BOOK -----------------------------------------------------
//   Parents can register kids/elders/spouse and track their vaccinations,
//   medications and allergies in one shared record.
const RELATIONS = [
  { k: "self",         l: "Self",           I: UserRound },
  { k: "spouse",       l: "Spouse",         I: UserRound },
  { k: "child",        l: "Child",          I: Baby },
  { k: "parent",       l: "Parent",         I: UserRound },
  { k: "grand_parent", l: "Grand parent",   I: UserRound },
  { k: "sibling",      l: "Sibling",        I: UserRound },
  { k: "other",        l: "Other",          I: UserRound },
];

function FamilyTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get("/family").then(r => setMembers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="family-tab">
      <div className="rounded-3xl bg-gradient-to-br from-rose-50 to-fuchsia-50 border border-rose-100 p-3 flex items-start gap-2">
        <Users size={14} className="text-rose-600 mt-0.5"/>
        <div className="text-xs text-stone-700">
          Keep one shared record for the whole family — kids' vaccinations, elders' medications, everyone's allergies. Private to your account.
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-stone-400"/></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="family-list">
            {members.map(m => {
              const R = RELATIONS.find(r => r.k === m.relation) || RELATIONS[0];
              return (
                <button
                  key={m.member_id}
                  onClick={() => setSelected(m)}
                  className="rounded-3xl bg-white border border-stone-200 hover:border-rose-300 hover:shadow-md text-left p-3 transition-all"
                  data-testid={`family-member-${m.member_id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-2xl bg-rose-50 grid place-items-center shrink-0"><R.I size={18} className="text-rose-600"/></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-stone-900 truncate">{m.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{m.relation.replace("_", " ")}{m.dob && ` · dob ${m.dob}`}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1 flex-wrap text-[10px]">
                    {m.blood_group && <span className="bg-rose-100 text-rose-800 font-black rounded-full px-2 py-0.5">🩸 {m.blood_group}</span>}
                    <span className="bg-emerald-100 text-emerald-800 font-black rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Syringe size={9}/> {m.vaccinations_count}</span>
                    <span className="bg-amber-100 text-amber-800 font-black rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Pill size={9}/> {m.medications_active_count} active</span>
                    {(m.allergies || []).slice(0, 2).map(a => <span key={a} className="bg-stone-100 text-stone-700 font-bold rounded-full px-2 py-0.5">⚠ {a}</span>)}
                  </div>
                </button>
              );
            })}
            <button
              onClick={() => setShowAdd(true)}
              className="rounded-3xl bg-white border-2 border-dashed border-stone-300 hover:border-rose-400 hover:bg-rose-50 p-3 text-left transition-all flex items-center gap-2"
              data-testid="family-add-btn"
            >
              <div className="w-10 h-10 rounded-2xl bg-rose-50 grid place-items-center"><Plus size={18} className="text-rose-600"/></div>
              <div>
                <div className="font-bold text-stone-900">Add a family member</div>
                <div className="text-[11px] text-stone-500">Kids, spouse, parents, grand-parents…</div>
              </div>
            </button>
          </div>

          {selected && <FamilyMemberDetail member={selected} onClose={() => { setSelected(null); load(); }}/>}
          {showAdd && <FamilyAddModal onClose={saved => { setShowAdd(false); if (saved) load(); }}/>}
        </>
      )}
    </div>
  );
}


function FamilyAddModal({ onClose }) {
  const [form, setForm] = useState({ name: "", relation: "child", dob: "", sex: "", blood_group: "", allergies: "", chronic: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/family", {
        ...form,
        allergies: form.allergies.split(",").map(s => s.trim()).filter(Boolean),
        chronic:   form.chronic.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Added");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" data-testid="family-add-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">Add family member</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-full text-sm" data-testid="family-name"/>
        <Select value={form.relation} onValueChange={v => setForm({ ...form, relation: v })}>
          <SelectTrigger className="rounded-full text-sm" data-testid="family-relation"><SelectValue placeholder="Relation"/></SelectTrigger>
          <SelectContent>{RELATIONS.map(r => <SelectItem key={r.k} value={r.k}>{r.l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" placeholder="DOB" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} className="rounded-full text-sm" data-testid="family-dob"/>
          <Select value={form.sex} onValueChange={v => setForm({ ...form, sex: v })}>
            <SelectTrigger className="rounded-full text-sm" data-testid="family-sex"><SelectValue placeholder="Sex"/></SelectTrigger>
            <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
          </Select>
        </div>
        <Input placeholder="Blood group (A+, O-, ...)" value={form.blood_group} onChange={e => setForm({ ...form, blood_group: e.target.value })} className="rounded-full text-sm" data-testid="family-blood"/>
        <Input placeholder="Allergies (comma-separated)"     value={form.allergies}  onChange={e => setForm({ ...form, allergies: e.target.value })}  className="rounded-full text-sm" data-testid="family-allergies"/>
        <Input placeholder="Chronic conditions (comma-separated)" value={form.chronic} onChange={e => setForm({ ...form, chronic: e.target.value })} className="rounded-full text-sm" data-testid="family-chronic"/>
        <Textarea rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-2xl text-sm" data-testid="family-notes"/>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-rose-600 hover:bg-rose-700 font-black h-9" data-testid="family-save">
          {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Plus size={13} className="mr-1"/>} Add member
        </Button>
      </div>
    </div>
  );
}


function FamilyMemberDetail({ member, onClose }) {
  const [tab, setTab] = useState("vax");
  const [vax, setVax] = useState([]);
  const [meds, setMeds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newVax, setNewVax] = useState({ name: "", given_on: "", next_due_on: "", notes: "" });
  const [newMed, setNewMed] = useState({ name: "", dosage: "", schedule: "", start_on: "", end_on: "", notes: "" });

  const loadVax  = () => api.get(`/family/${member.member_id}/vaccinations`).then(r => setVax(r.data));
  const loadMeds = () => api.get(`/family/${member.member_id}/medications`).then(r => setMeds(r.data));
  useEffect(() => { loadVax(); loadMeds(); }, []);   // eslint-disable-line

  const addVax = async () => {
    if (!newVax.name.trim()) return;
    setBusy(true);
    try { await api.post(`/family/${member.member_id}/vaccinations`, newVax);
          setNewVax({ name: "", given_on: "", next_due_on: "", notes: "" }); loadVax(); toast.success("Vaccination logged"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const addMed = async () => {
    if (!newMed.name.trim()) return;
    setBusy(true);
    try { await api.post(`/family/${member.member_id}/medications`, { ...newMed, active: true });
          setNewMed({ name: "", dosage: "", schedule: "", start_on: "", end_on: "", notes: "" }); loadMeds(); toast.success("Medication added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggleMed = async (m) => { await api.patch(`/family/medications/${m.med_id}`, { active: !m.active }); loadMeds(); };
  const delMember = async () => {
    if (!window.confirm(`Remove ${member.name} from family?`)) return;
    await api.delete(`/family/${member.member_id}`);
    toast.success("Removed"); onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-2xl space-y-3 max-h-[92vh] overflow-y-auto" data-testid={`family-detail-${member.member_id}`}>
        <div className="flex items-center gap-3 pb-2 border-b border-stone-100">
          <div className="w-10 h-10 rounded-2xl bg-rose-50 grid place-items-center shrink-0"><UserRound size={18} className="text-rose-600"/></div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-stone-900">{member.name}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{member.relation.replace("_", " ")}{member.dob && ` · dob ${member.dob}`}</div>
          </div>
          <button onClick={delMember} className="text-stone-400 hover:text-red-600" data-testid={`family-del-${member.member_id}`}><Trash2 size={14}/></button>
          <FamilyLockButton member={member} onChange={onClose}/>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>

        <div className="flex gap-1">
          {[{ k: "vax", l: "Vaccinations", I: Syringe }, { k: "meds", l: "Medications", I: Pill }].map(({ k, l, I }) => (
            <button key={k} onClick={() => setTab(k)} className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1 border ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`} data-testid={`family-tab-${k}`}>
              <I size={11}/> {l}
            </button>
          ))}
        </div>

        {tab === "vax" && (
          <div className="space-y-2" data-testid="family-vax-list">
            {vax.length === 0 && <div className="text-sm text-stone-500 text-center py-4">No vaccinations logged yet.</div>}
            {vax.map(v => (
              <div key={v.vax_id} className="rounded-2xl border border-stone-200 p-2.5 flex items-start gap-2" data-testid={`family-vax-${v.vax_id}`}>
                <Syringe size={13} className="text-emerald-600 mt-0.5 shrink-0"/>
                <div className="flex-1">
                  <div className="font-bold text-stone-900">{v.name}</div>
                  <div className="text-[11px] text-stone-500">Given {v.given_on || "—"}{v.next_due_on && ` · next due ${v.next_due_on}`}</div>
                  {v.notes && <div className="text-[11px] text-stone-600">{v.notes}</div>}
                </div>
                <button onClick={async () => { await api.delete(`/family/vaccinations/${v.vax_id}`); loadVax(); }} className="text-stone-400 hover:text-rose-600" data-testid={`family-vax-del-${v.vax_id}`}><Trash2 size={12}/></button>
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-stone-300 p-2.5 space-y-1.5" data-testid="family-vax-add">
              <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Add vaccination</div>
              <Input placeholder="Vaccine name (e.g. BCG, Polio, Covishield)" value={newVax.name} onChange={e => setNewVax({ ...newVax, name: e.target.value })} className="rounded-full text-sm h-8"/>
              <div className="grid grid-cols-2 gap-1.5">
                <Input type="date" placeholder="Given on" value={newVax.given_on} onChange={e => setNewVax({ ...newVax, given_on: e.target.value })} className="rounded-full text-sm h-8"/>
                <Input type="date" placeholder="Next due" value={newVax.next_due_on} onChange={e => setNewVax({ ...newVax, next_due_on: e.target.value })} className="rounded-full text-sm h-8"/>
              </div>
              <Input placeholder="Notes" value={newVax.notes} onChange={e => setNewVax({ ...newVax, notes: e.target.value })} className="rounded-full text-sm h-8"/>
              <Button onClick={addVax} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-8" data-testid="family-vax-save"><Plus size={12} className="mr-1"/> Log</Button>
            </div>
          </div>
        )}

        {tab === "meds" && (
          <div className="space-y-2" data-testid="family-meds-list">
            {meds.length === 0 && <div className="text-sm text-stone-500 text-center py-4">No medications yet.</div>}
            {meds.map(m => (
              <div key={m.med_id} className={`rounded-2xl border p-2.5 flex items-start gap-2 ${m.active ? "border-amber-200 bg-amber-50/30" : "border-stone-200 opacity-60"}`} data-testid={`family-med-${m.med_id}`}>
                <Pill size={13} className={`mt-0.5 shrink-0 ${m.active ? "text-amber-600" : "text-stone-400"}`}/>
                <div className="flex-1">
                  <div className="font-bold text-stone-900">{m.name}{m.dosage && <span className="text-stone-500 font-normal ml-1">· {m.dosage}</span>}</div>
                  <div className="text-[11px] text-stone-500">{m.schedule || "as directed"}{m.start_on && ` · from ${m.start_on}`}{m.end_on && ` · until ${m.end_on}`}</div>
                  {m.notes && <div className="text-[11px] text-stone-600">{m.notes}</div>}
                </div>
                <button onClick={() => toggleMed(m)} className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${m.active ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-500"}`} data-testid={`family-med-toggle-${m.med_id}`}>
                  {m.active ? "active" : "stopped"}
                </button>
                <button onClick={async () => { await api.delete(`/family/medications/${m.med_id}`); loadMeds(); }} className="text-stone-400 hover:text-rose-600" data-testid={`family-med-del-${m.med_id}`}><Trash2 size={12}/></button>
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-stone-300 p-2.5 space-y-1.5" data-testid="family-med-add">
              <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Add medication</div>
              <Input placeholder="Name (e.g. Paracetamol)" value={newMed.name} onChange={e => setNewMed({ ...newMed, name: e.target.value })} className="rounded-full text-sm h-8"/>
              <div className="grid grid-cols-2 gap-1.5">
                <Input placeholder="Dosage (500 mg)" value={newMed.dosage} onChange={e => setNewMed({ ...newMed, dosage: e.target.value })} className="rounded-full text-sm h-8"/>
                <Input placeholder="Schedule (twice daily)" value={newMed.schedule} onChange={e => setNewMed({ ...newMed, schedule: e.target.value })} className="rounded-full text-sm h-8"/>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <Input type="date" value={newMed.start_on} onChange={e => setNewMed({ ...newMed, start_on: e.target.value })} className="rounded-full text-sm h-8"/>
                <Input type="date" value={newMed.end_on}   onChange={e => setNewMed({ ...newMed, end_on: e.target.value })}   className="rounded-full text-sm h-8"/>
              </div>
              <Input placeholder="Notes" value={newMed.notes} onChange={e => setNewMed({ ...newMed, notes: e.target.value })} className="rounded-full text-sm h-8"/>
              <Button onClick={addMed} disabled={busy} className="rounded-full bg-amber-600 hover:bg-amber-700 font-black h-8" data-testid="family-med-save"><Plus size={12} className="mr-1"/> Add</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── FAMILY NFC LOCKER ------------------------------------------------------
//   Reuses the Doc-Lock pattern so parents can hide vaccination + medication
//   records behind an NFC key tap. When locked, a modal asks for the tap
//   (or a paste of the tag UID) before revealing the details.
function FamilyLockButton({ member, onChange }) {
  const [tags, setTags]     = useState([]);
  const [showPicker, setPicker] = useState(false);
  const [uid, setUid]       = useState("");
  const [busy, setBusy]     = useState(false);
  const locked = !!member.nfc_locked;

  useEffect(() => {
    if (showPicker) api.get("/nfc/tags").then(r => setTags(r.data)).catch(() => {});
  }, [showPicker]);

  const applyLock = async (tag) => {
    setBusy(true);
    try {
      await api.patch(`/nfc/tags/${tag.tag_id}/lock-family/${member.member_id}`);
      toast.success(`Locked to ${tag.label || tag.tag_id}`);
      setPicker(false);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Lock failed"); }
    finally { setBusy(false); }
  };

  const removeLock = async () => {
    if (!window.confirm("Permanently unlock this family record?")) return;
    setBusy(true);
    try {
      await api.delete(`/nfc/tags/${member.nfc_unlock_tag_id}/lock-family/${member.member_id}`);
      toast.success("Unlocked");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const unlockOnce = async () => {
    if (!uid.trim()) { toast.error("Tap a tag or paste its UID"); return; }
    setBusy(true);
    try {
      const r = await api.post("/nfc/family/unlock", { member_id: member.member_id, uid: uid.trim() });
      toast.success(`Unlocked · valid ${Math.floor((r.data.expires_in_sec || 300) / 60)} min`);
      setPicker(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Wrong key"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => (locked ? removeLock() : setPicker(true))}
        className={`text-stone-400 hover:${locked ? "text-emerald-600" : "text-amber-600"}`}
        title={locked ? "NFC-locked · click to remove" : "Lock behind an NFC key"}
        data-testid={`family-lock-${member.member_id}`}
        disabled={busy}
      >
        {locked ? <Lock size={14} className="text-amber-600"/> : <LockOpen size={14}/>}
      </button>
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => setPicker(false)}>
          <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3" data-testid="family-lock-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-stone-900">{locked ? "Unlock family record" : "Lock behind an NFC key"}</h3>
              <button onClick={() => setPicker(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
            </div>
            <p className="text-xs text-stone-500">Only your programmed NFC tag can reveal this record. Great for kids' vaccinations, elders' medications, and other private health data.</p>
            {locked ? (
              <>
                <Input value={uid} onChange={e => setUid(e.target.value)} placeholder="Tap your key or paste UID" className="rounded-full text-sm font-mono" data-testid="family-unlock-uid"/>
                <Button onClick={unlockOnce} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9 w-full" data-testid="family-unlock-btn"><LockOpen size={13} className="mr-1"/> Unlock once (5 min)</Button>
              </>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-widest font-black text-stone-500">Choose an NFC tag</div>
                <div className="rounded-2xl border border-stone-200 max-h-64 overflow-y-auto divide-y divide-stone-100">
                  {tags.length === 0 && <div className="p-3 text-sm text-stone-500 text-center">No tags yet. Link one at <a className="text-emerald-700 font-bold hover:underline" href="/nfc">/nfc</a>.</div>}
                  {tags.map(t => (
                    <button key={t.tag_id} onClick={() => applyLock(t)} className="w-full text-left p-3 hover:bg-stone-50 flex items-center gap-2" data-testid={`family-lock-tag-${t.tag_id}`} disabled={busy}>
                      <Lock size={14} className="text-amber-600"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-stone-900 truncate">{t.label || t.tag_id}</div>
                        <div className="text-[11px] text-stone-500 font-mono truncate">{t.tag_id}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

